// CustomerDashboard.jsx - Mobile Optimized with PrimeReact UI
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
    Card, 
    Button, 
    DataTable, 
    Column, 
    Tag, 
    ProgressBar,
    Toast,
    Avatar,
    Dialog,
    Dropdown,
    Calendar,
    InputNumber,
    InputTextarea,
    Badge,
    Panel,
    Divider
} from 'primereact';

import { useNavigate } from 'react-router-dom';
import { format, formatDistanceToNow, isToday } from 'date-fns';
import useApi from "hooks/useApi";
import useAuth from "hooks/useAuth";
import useApp from "hooks/useApp";
import QuickBooking from './QuickBooking';

const API_URL = process.env.REACT_APP_API_PATH || 'http://localhost:5000/api';

const CustomerDashboard = () => {
    const auth = useAuth();
    const app = useApp();
    const api = useApi();
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showQuickBookingModal, setShowQuickBookingModal] = useState(false); // Add this state
    const [totalDue, setTotalDue] = useState(0);
    const toast = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        loadDashboardData();
        loadTotalDue();
    }, []);

    const loadDashboardData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}customer/dashboard`);
            
            if (response.data.status === 'success') {
                setDashboardData(response.data.data);
            } else {
                showError(response.data.message || 'Failed to load dashboard');
            }
        } catch (error) {
            console.error('Error loading dashboard:', error);
            showError('Failed to load dashboard data. Please check your connection.');
        } finally {
            setLoading(false);
        }
    };

    const loadTotalDue = async () => {
        try {
            const response = await axios.get(`${API_URL}/customer/payments/total-due`);
            if (response.data.status === 'success') {
                setTotalDue(response.data.totalDue || 0);
            }
        } catch (error) {
            console.error('Error loading total due:', error);
            setTotalDue(0);
        }
    };

    const showError = (message) => {
        if (toast.current) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: message,
                life: 3000
            });
        }
    };

    const showSuccess = (message) => {
        if (toast.current) {
            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: message,
                life: 3000
            });
        }
    };

    const getStatusSeverity = (status) => {
        if (!status) return null;
        const statusLower = status.toLowerCase();
        if (statusLower.includes('complete')) return 'success';
        if (statusLower.includes('process') || statusLower.includes('progress')) return 'info';
        if (statusLower.includes('pending')) return 'warning';
        if (statusLower.includes('cancel')) return 'danger';
        return 'info';
    };

    const handleQuickBookingSuccess = () => {
        setShowQuickBookingModal(false);
        showSuccess('Booking created successfully!');
        loadDashboardData();
        loadTotalDue();
    };

    const handlePayNow = () => {
        navigate('/payments/paymentcustomer');
    };

    if (loading) {
        return (
            <div className="grid p-fluid">
                <div className="col-12">
                    <Card className="border-round shadow-2">
                        <div className="flex flex-column align-items-center justify-content-center py-8">
                            <ProgressBar mode="indeterminate" style={{ height: '6px', width: '200px' }} className="mb-3"></ProgressBar>
                            <span className="text-primary font-medium">Loading dashboard...</span>
                        </div>
                    </Card>
                </div>
            </div>
        );
    }

    if (!dashboardData) {
        return (
            <div className="flex flex-column align-items-center justify-content-center min-h-screen p-4">
                <Card className="shadow-3 text-center p-4">
                    <i className="pi pi-exclamation-triangle text-6xl text-yellow-500 mb-4"></i>
                    <h3 className="text-2xl font-medium mb-3">No Dashboard Data</h3>
                    <p className="text-gray-600 mb-4">Unable to load dashboard data. Please try again.</p>
                    <Button 
                        label="Retry" 
                        icon="pi pi-refresh" 
                        onClick={loadDashboardData}
                        className="p-button-lg"
                    />
                </Card>
            </div>
        );
    }

    const { user, stats, activebookings = [], recentbookings = [], upcomingpickups = [] } = dashboardData;

    // Mobile responsive render functions
    const renderMobileStats = () => (
        <div className="grid">
            {[
                { label: 'Total Bookings', value: stats?.totalbookings || 0, icon: 'pi-shopping-bag', color: 'primary' },
                { label: 'Completed', value: stats?.completedbookings || 0, icon: 'pi-check-circle', color: 'green' },
                { label: 'Active', value: stats?.activebookings || 0, icon: 'pi-spinner', color: 'orange' },
                { label: 'Total Spent', value: `$${(stats?.totalspent || 0).toFixed(2)}`, icon: 'pi-dollar', color: 'purple' }
            ].map((stat, index) => (
                <div key={index} className="col-6 mb-3">
                    <Card className="shadow-2 border-round-xl">
                        <div className="flex align-items-center p-2">
                            <div className="flex align-items-center justify-content-center border-circle mr-3" 
                                style={{ 
                                    backgroundColor: `var(--${stat.color}-50)`, 
                                    width: '48px', 
                                    height: '48px' 
                                }}>
                                <i className={`pi ${stat.icon} text-${stat.color}-500`} style={{ fontSize: '1.5rem' }}></i>
                            </div>
                            <div className="flex flex-column">
                                <span className="text-2xl font-bold">{stat.value}</span>
                                <span className="text-sm text-500">{stat.label}</span>
                            </div>
                        </div>
                    </Card>
                </div>
            ))}
        </div>
    );

    const renderMobileBookingItem = (booking, isActive = true) => (
        <div key={booking.id} className="surface-card border-round-xl p-3 mb-3 shadow-2">
            <div className="flex justify-content-between align-items-start mb-2">
                <div className="flex-grow-1">
                    <div className="flex align-items-center gap-2 mb-2">
                        <span className="font-semibold text-lg">{booking.service?.name || 'Service'}</span>
                        <Tag 
                            value={booking.status} 
                            severity={getStatusSeverity(booking.status)}
                            className="text-xs px-2 py-1"
                            rounded
                        />
                    </div>
                    <div className="flex align-items-center gap-2 text-sm text-600 mb-1">
                        <i className="pi pi-hashtag text-primary"></i>
                        <span>{booking.bookingreference}</span>
                    </div>
                    <div className="flex align-items-center gap-2 text-sm text-600">
                        <i className="pi pi-calendar text-primary"></i>
                        <span>
                            Pickup: {booking.pickupdate ? format(new Date(booking.pickupdate), 'MMM dd, yyyy') : 'N/A'}
                            {booking.pickupdate && isToday(new Date(booking.pickupdate)) && 
                                <Badge value="Today" severity="success" className="ml-2"></Badge>}
                        </span>
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-2xl font-bold text-primary mb-2">₹{(booking.totalamount || 0).toFixed(2)}</div>
                    <Button 
                        icon="pi pi-eye" 
                        className="p-button-rounded p-button-outlined p-button-sm"
                        onClick={() => navigate(`/bookings/view/${booking.id}`)}
                        tooltip="View Details"
                        tooltipOptions={{ position: 'left' }}
                    />
                </div>
            </div>
        </div>
    );

    const renderMobileUpcomingPickup = (booking, index) => (
        <div key={booking.id || index} className="surface-card border-round-xl p-3 mb-3 shadow-2">
            <div className="flex align-items-start">
                <div className="flex align-items-center justify-content-center border-circle bg-primary-50 mr-3" 
                    style={{ width: '40px', height: '40px' }}>
                    <i className="pi pi-calendar text-primary-600"></i>
                </div>
                <div className="flex-grow-1">
                    <div className="flex align-items-center justify-content-between mb-2">
                        <span className="font-semibold text-lg">{booking.service?.name || 'Service'}</span>
                        <Tag 
                            value={booking.status} 
                            severity={getStatusSeverity(booking.status)}
                            className="text-xs"
                            rounded
                        />
                    </div>
                    <div className="flex align-items-center gap-2 text-sm text-600 mb-1">
                        <i className="pi pi-clock text-primary"></i>
                        <span>
                            {booking.pickupdate ? 
                                format(new Date(booking.pickupdate), 'EEE, MMM dd • hh:mm a') : 'Date not set'}
                        </span>
                    </div>
                    <div className="flex align-items-center gap-2 text-sm text-600">
                        <i className="pi pi-map-marker text-primary"></i>
                        <span className="text-600">{booking.pickupaddress || 'Address not specified'}</span>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="customer-dashboard">
            <Toast ref={toast} position="top-center" />
            
            {/* Mobile Header */}
            <div className="surface-card shadow-3 mb-4" style={{ borderRadius: '0 0 2rem 2rem' }}>
                <div className="p-4">
                    <div className="flex align-items-center justify-content-between mb-3">
                        <div>
                            <span className="text-500 text-sm font-medium">Welcome back,</span>
                            <h1 className="text-3xl font-bold text-primary mt-1">{user?.fullname?.split(' ')[0] || 'Customer'}!</h1>
                            <p className="text-600 mt-2">Your laundry dashboard</p>
                        </div>
                        <div className="relative">
                            <Avatar 
                                icon="pi pi-user" 
                                size="xlarge"
                                className="bg-primary text-white shadow-2"
                                style={{ width: '64px', height: '64px', fontSize: '2rem' }}
                            />
                            {stats?.activebookings > 0 && (
                                <Badge 
                                    value={stats.activebookings} 
                                    severity="danger" 
                                    className="absolute"
                                    style={{ top: '0', right: '0' }}
                                ></Badge>
                            )}
                        </div>
                    </div>
                    
                    <div className="flex gap-3 mt-4">
                        <Button 
                        label="New Booking" 
                        icon="pi pi-plus" 
                        className="p-button-lg flex-1 shadow-2"
                        style={{ borderRadius: '50px' }}
                        onClick={() => navigate('/bookingquick')}
                        tooltip='Booking with Items'
                        />

                        <div className="">
                    <QuickBooking/>
                    </div>
                        {/* <Button 
                            icon="pi pi-bolt" 
                            className="p-button-outlined p-button-lg"
                            onClick={() => setShowQuickBookingModal(true)}
                            tooltip="Quick Book"
                            tooltipOptions={{ position: 'bottom' }}
                        /> */}
                    </div>
                </div>
            </div>

            <div className="px-3">
                {/* Mobile Stats */}
                {renderMobileStats()}

                {/* Total Due Card */}
                {totalDue > 0 && (
                    <Card className="mb-4 shadow-3 border-round-xl" style={{ 
                        background: 'linear-gradient(135deg, var(--orange-500) 0%, var(--orange-700) 100%)',
                        cursor: 'pointer'
                    }} onClick={handlePayNow}>
                        <div className="flex align-items-center justify-content-between p-2">
                            <div className="flex align-items-center">
                                <div className="flex align-items-center justify-content-center border-circle mr-3" 
                                    style={{ 
                                        backgroundColor: 'rgba(255,255,255,0.2)', 
                                        width: '48px', 
                                        height: '48px' 
                                    }}>
                                    <i className="pi pi-credit-card text-white" style={{ fontSize: '1.5rem' }}></i>
                                </div>
                                <div>
                                    <div className="text-white opacity-90 text-sm font-medium mb-1">Total Due</div>
                                    <div className="text-white text-2xl font-bold">₹{totalDue.toFixed(2)}</div>
                                </div>
                            </div>
                            <Button 
                                label="Pay Now" 
                                icon="pi pi-arrow-right" 
                                className="p-button-outlined p-button-sm text-white border-white"
                                style={{ borderColor: 'white', color: 'white' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handlePayNow();
                                }}
                            />
                        </div>
                    </Card>
                )}

                {/* Mobile Active Bookings */}
                <Panel header="Active Bookings" className="mb-4 shadow-2" toggleable>
                    {activebookings.length === 0 ? (
                        <div className="text-center py-6">
                            <i className="pi pi-inbox text-5xl text-300 mb-3"></i>
                            <p className="text-600 text-lg mb-4">No active bookings</p>
                            <Button 
                                label="Book Now" 
                                icon="pi pi-plus"
                                onClick={() => navigate('/bookingquick')}
                                className="p-button-lg"
                            />
                        </div>
                    ) : (
                        <div>
                            <div className="max-h-25rem overflow-auto">
                                {activebookings.map(booking => renderMobileBookingItem(booking, true))}
                            </div>
                            <Divider />
                            <Button 
                                label="View All Bookings" 
                                icon="pi pi-arrow-right"
                                className="p-button-text w-full p-button-lg"
                                onClick={() => navigate('/bookings/listcustomer')}
                            />
                        </div>
                    )}
                </Panel>

                {/* Mobile Upcoming Pickups */}
                <Panel header="Upcoming Pickups" className="mb-4 shadow-2" toggleable>
                    {upcomingpickups.length === 0 ? (
                        <div className="text-center py-5">
                            <i className="pi pi-calendar-times text-5xl text-300 mb-2"></i>
                            <p className="text-600">No upcoming pickups</p>
                        </div>
                    ) : (
                        <div>
                            {upcomingpickups.map((booking, index) => renderMobileUpcomingPickup(booking, index))}
                        </div>
                    )}
                </Panel>

                {/* Mobile Recent Bookings */}
                <Panel header="Recent Activity" className="mb-4 shadow-2" toggleable>
                    {recentbookings.length === 0 ? (
                        <div className="text-center py-5">
                            <i className="pi pi-history text-5xl text-300 mb-2"></i>
                            <p className="text-600">No recent activity</p>
                        </div>
                    ) : (
                        <div>
                            {recentbookings.slice(0, 3).map(booking => (
                                <div key={booking.id} className="flex justify-content-between align-items-center p-3 surface-card border-round-xl mb-2 shadow-1">
                                    <div>
                                        <div className="font-semibold text-lg mb-1">{booking.service?.name || 'Service'}</div>
                                        <div className="flex align-items-center gap-2 text-sm text-600">
                                            <i className="pi pi-clock"></i>
                                            <span>
                                                {booking.createdat ? 
                                                    formatDistanceToNow(new Date(booking.createdat), { addSuffix: true }) : ''}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-xl font-bold text-primary mb-2">₹{(booking.totalamount || 0).toFixed(2)}</div>
                                        <Tag 
                                            value={booking.status} 
                                            severity={getStatusSeverity(booking.status)}
                                            rounded
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Panel>

                {/* Mobile Quick Actions */}
                <Panel header="Quick Actions" className="mb-4 shadow-2">
                    <div className="grid">
                        <div className="col-6 mb-3">
                            <Button 
                                label="Schedule" 
                                icon="pi pi-truck" 
                                className="p-button-outlined p-button-lg w-full"
                                onClick={() => navigate('/bookingquick')}
                            />
                        </div>
                        <div className="col-6 mb-3">
                            <Button 
                                label="Bookings" 
                                icon="pi pi-list" 
                                className="p-button-outlined p-button-lg w-full"
                                onClick={() => navigate('/bookings/listcustomer')}
                            />
                        </div>
                        <div className="col-6">
                            <Button 
                                label="Payments" 
                                icon="pi pi-wallet" 
                                className="p-button-outlined p-button-lg w-full"
                                onClick={() => navigate('/payments/paymentcustomer')}
                            />
                        </div>
                        <div className="col-6">
                            <Button 
                                label="Profile" 
                                icon="pi pi-user-edit" 
                                className="p-button-outlined p-button-lg w-full"
                                onClick={() => navigate('/account')}
                            />
                        </div>
                    </div>
                </Panel>

                {/* Mobile Loyalty Points */}
                <Card className="mb-5 shadow-3" style={{ background: 'linear-gradient(135deg, var(--primary-600) 0%, var(--primary-800) 100%)' }}>
                    <div className="text-center py-4">
                        <i className="pi pi-star text-5xl text-yellow-300 mb-3"></i>
                        <h3 className="text-white text-2xl font-bold mb-2">Loyalty Points</h3>
                        <div className="text-white text-5xl font-bold mb-2">{user?.loyaltypoints || 0}</div>
                        <div className="text-white text-sm opacity-80 mb-4">Points Available</div>
                        
                        <div className="mb-4 px-4">
                            <ProgressBar 
                                value={(user?.loyaltypoints || 0) % 100} 
                                showValue={false}
                                style={{ height: '10px', backgroundColor: 'rgba(255,255,255,0.2)' }}
                                className="mb-2"
                                color="var(--yellow-300)"
                            />
                            <div className="text-white text-sm">
                                {Math.min((user?.loyaltypoints || 0) % 100, 100)}/100 to next reward
                            </div>
                        </div>
                        
                        <Button 
                            label="Redeem Points" 
                            icon="pi pi-gift" 
                            className="p-button-outlined p-button-lg text-white border-white"
                            disabled={(user?.loyaltypoints || 0) < 100}
                            style={{ borderColor: 'white', color: 'white' }}
                        />
                    </div>
                </Card>
            </div>

            {/* Mobile Bottom Navigation */}
            <div 
                className="surface-card shadow-4" 
                style={{ 
                    position: 'fixed', 
                    bottom: 0, 
                    left: 0, 
                    right: 0,
                    zIndex: 1000
                }}>
                <div className="flex justify-content-around p-3">
                    {[
                        { icon: 'pi-home', label: 'Home', path: '/', color: 'primary' },
                        { icon: 'pi-calendar-plus', label: 'Book', path: '/bookingquick', color: 'orange' },
                        { icon: 'pi-bell', label: 'Alerts', path: '/notifications', color: 'purple' },
                        { icon: 'pi-user', label: 'Profile', path: '/profile', color: 'green' }
                    ].map((item, index) => (
                        <div key={index} className="text-center">
                            <Button 
                                icon={item.icon} 
                                className="p-button-rounded p-button-lg"
                                onClick={() => navigate(item.path)}
                                style={{ 
                                    width: '3.5rem',
                                    height: '3.5rem',
                                    borderRadius: '50%',
                                    backgroundColor: `var(--${item.color}-50)`,
                                    border: 'none',
                                    color: `var(--${item.color}-500)`
                                }}
                            />
                            <div className="text-xs mt-2 text-600">{item.label}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Quick Booking Modal Dialog */}
            <Dialog
                header={
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-bolt text-primary" style={{ fontSize: '1.5rem' }}></i>
                        <span className="text-xl font-bold">Quick Booking</span>
                    </div>
                }
                visible={showQuickBookingModal}
                onHide={() => setShowQuickBookingModal(false)}
                className="w-11"
                breakpoints={{ '960px': '75vw', '640px': '95vw' }}
                style={{ borderRadius: '2rem' }}
                modal
                dismissableMask
            >
                <QuickBooking 
                    onSuccess={handleQuickBookingSuccess}
                    onCancel={() => setShowQuickBookingModal(false)}
                />
            </Dialog>

            <style>{`
                .customer-dashboard {
                    padding-bottom: 90px;
                    background: var(--surface-ground);
                    min-height: 100vh;
                }
                
                .max-h-25rem {
                    max-height: 25rem;
                }
                
                /* Override PrimeReact default styles for vela-blue theme */
                .p-card {
                    border-radius: 1rem;
                }
                
                .p-panel .p-panel-header {
                    border-radius: 1rem 1rem 0 0;
                    padding: 1rem;
                    font-size: 1.25rem;
                }
                
                .p-panel .p-panel-content {
                    border-radius: 0 0 1rem 1rem;
                }
                
                .p-button.p-button-lg {
                    padding: 0.75rem 1.5rem;
                    font-size: 1rem;
                }
                
                .p-button.p-button-text.p-button-lg {
                    padding: 0.5rem;
                }
                
                .p-dialog .p-dialog-header {
                    border-radius: 2rem 2rem 0 0;
                    padding: 1.5rem;
                }
                
                .p-dialog .p-dialog-content {
                    border-radius: 0 0 2rem 2rem;
                    padding: 1.5rem;
                }
                
                /* Custom utility classes */
                .border-round-xl {
                    border-radius: 1rem;
                }
                
                .shadow-2 {
                    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
                }
                
                .shadow-3 {
                    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
                }
                
                .shadow-4 {
                    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.2);
                }
                
                /* Touch-friendly tap targets */
                @media (max-width: 768px) {
                    .p-button {
                        min-height: 48px;
                    }
                    
                    .p-button.p-button-lg {
                        min-height: 56px;
                    }
                    
                    .p-inputtext {
                        min-height: 48px;
                    }
                    
                    .p-inputtext-lg {
                        min-height: 56px;
                        font-size: 1rem;
                    }
                    
                    .text-2xl {
                        font-size: 1.75rem;
                    }
                    
                    .text-3xl {
                        font-size: 2rem;
                    }
                    
                    .text-4xl {
                        font-size: 2.25rem;
                    }
                    
                    .text-5xl {
                        font-size: 2.5rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default CustomerDashboard;
